import {
  URL_ACCOUNT_REGISTRATION,
  URL_BLOG_HIRING_ADVICE_ROOT,
  URL_ROOT,
} from '@seek/adv-constants';
import { ManagedContentBanner } from '@seek/cms-ui';
import { useTrackView } from '@seek/cmsu-analytics';
import { PageComponent } from '@seek/cmsu-components';
import type { Country, Language } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Column,
  Columns,
  Box,
  ContentBlock,
  Text,
  Stack,
  Alert,
} from 'braid-design-system';
import { useEffect } from 'react';

import { analyticsEvents } from 'src/constants/analytics';
import {
  META_TITLE_HOME,
  META_DESCRIPTION_HOME,
  getMetaDataBySite,
} from 'src/constants/pageMetaData';
import { HOME_PAGE_VIEW } from 'src/constants/pageViewUniqueKeys';
import { useConfig } from 'src/hooks/context';
import {
  FEATURE_CMS_PAGE_CONTENT,
  useFeatureToggleData,
} from 'src/modules/FeatureToggler';
import { type CmsPageContent, ModuleTheme } from 'src/types';
import {
  blockItemRenderers,
  customBannerRenderers,
} from 'src/views/pages/pageRenderers';
// shared components
import ContentCentredColumn from 'src/views/shared/ContentCentredColumn/ContentCentredColumn';
import ContentSplitThreeColumn from 'src/views/shared/ContentSplitThreeColumn/ContentSplitThreeColumn';
import IconContentBox from 'src/views/shared/IconContentBox/IconContentBox';
import ImageContentSplit from 'src/views/shared/ImageContentSplit/ImageContentSplit';
import IframeContainer from 'src/views/shared/MediaContainers/IframeContainer';
import SeoMeta from 'src/views/shared/SeoMeta/SeoMeta';
import TabsContainer from 'src/views/shared/Tabs/TabsContainer';
import Testimonial from 'src/views/shared/Testimonial/Testimonial';

import translations from './.vocab';
import Hero from './components/Hero/Hero';
import MarketingBanner from './components/MarketingBanner/MarketingBanner';
// icons & images
import Icon1 from './images/Icon1';
import Icon2 from './images/Icon2';
import Icon3 from './images/Icon3';
import mediaAdviceImage from './images/hiring-advice-image.jpg';
import multipurposeImage from './images/talent-search-screenshot.png';

interface HomePageProps {
  content?: CmsPageContent;
}

const HomePage = ({ content }: HomePageProps) => {
  const {
    absoluteEmployerUrlResolver,
    country,
    environment,
    isAnz,
    language,
    locale,
    site,
    urlResolver,
  } = useConfig();
  const { t } = useTranslations(translations);

  const isCmsPageContentEnabled = useFeatureToggleData(
    FEATURE_CMS_PAGE_CONTENT,
  );

  const trackFunction = useTrackView(HOME_PAGE_VIEW);

  useEffect(() => {
    if (trackFunction) {
      trackFunction();
    }

    if (isCmsPageContentEnabled) {
      // eslint-disable-next-line no-console
      console.log('@@@ Home | content: ', content);
    }

    // TEMP: Hide CMS testimonials for non ANZ sites
    // This can be removed once testimonials become available across all APAC sites in CMS
    const element = document.getElementById('testimonials');
    if (element) {
      if (!isAnz) {
        element.style.display = 'none';
      } else {
        element.style.display = '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fullURL = absoluteEmployerUrlResolver(URL_ROOT);

  return (
    <>
      <ManagedContentBanner
        platform="employer"
        context="hirer_shopfront"
        environment={environment}
        locale={locale}
      />

      {isCmsPageContentEnabled ? (
        <>
          {content ? (
            <>
              <SeoMeta
                title={content.seo?.title ?? META_TITLE_HOME}
                description={content.seo?.description ?? ''}
                url={fullURL}
              />
              <PageComponent
                language={language as Language}
                melwaysLocale={locale}
                country={country.toLowerCase() as Country}
                fullURL={fullURL}
                // @ts-expect-error
                page={content}
                wptSalesForceApi=""
                subscriptionFormPrivacyUrl=""
                subscriptionFormSourceName=""
                renderers={blockItemRenderers}
                bannerRenderers={customBannerRenderers}
              />
            </>
          ) : (
            // This _should_ never happen but in case it does, let the user know
            <Box marginTop={{ mobile: 'large', desktop: 'xxlarge' }}>
              <ContentBlock>
                <Alert tone="critical">
                  <Stack space="small">
                    <Text weight="strong">Error loading page content</Text>
                    <Text>
                      We were unable to fetch the content for this page. Please
                      try again or check back later.
                    </Text>
                  </Stack>
                </Alert>
              </ContentBlock>
            </Box>
          )}
        </>
      ) : (
        <>
          <SeoMeta
            title={META_TITLE_HOME}
            description={getMetaDataBySite(site, META_DESCRIPTION_HOME)}
            url={fullURL}
          />
          <Hero />
          <ContentCentredColumn
            id="testimonial"
            theme={ModuleTheme.DARK}
            title={t('Testimonial title')}
            buttonLinkHref={urlResolver({ path: URL_ACCOUNT_REGISTRATION })}
            buttonLinkEvent={analyticsEvents.TRACK_CTA}
            buttonLinkText={t('Testimonial button')}
          >
            <TabsContainer
              analyticsOrigin="testimonial"
              tabItems={[
                {
                  name: t('Fast results title'),
                  analyticsEvent: analyticsEvents.TRACK_FAST_RESULT,
                },
                {
                  name: t('Quality candidates title'),
                  analyticsEvent: analyticsEvents.TRACK_QUALITY_CANDIDATES,
                },
                {
                  name: t('Simple process title'),
                  analyticsEvent: analyticsEvents.TRACK_EASY_PROCESS,
                },
                {
                  name: t('Powerful tools title'),
                  analyticsEvent: analyticsEvents.TRACK_POWERFUL_TOOLS,
                },
              ]}
            >
              <Columns space="small" collapseBelow="tablet">
                <Column>
                  <IframeContainer src="https://players.brightcove.net/6040621522001/qx6UKDb3d_default/index.html?videoId=6051849844001" />
                </Column>
                <Column>
                  <Box display="flex" alignItems="center" height="full">
                    <Testimonial
                      quote={t('Fast results quote')}
                      name="David Bennett"
                      role={t('Fast results role')}
                      company="Dab Transport"
                    />
                  </Box>
                </Column>
              </Columns>
              <Testimonial
                quote={t('Quality candidates quote')}
                name="Brett Witte"
                role={t('Quality candidates role')}
                company="Allura Homes"
              />
              <Testimonial
                quote={t('Simple process quote')}
                name="Sheron White"
                role={t('Simple process role')}
                company="Direct Publishing"
              />
              <Testimonial
                quote={t('Powerful tools quote')}
                name="Karen Meissner"
                role={t('Powerful tools role')}
                company="Brand 4 Profit"
              />
            </TabsContainer>
          </ContentCentredColumn>

          <ContentSplitThreeColumn
            id="content-split-on-3"
            theme={ModuleTheme.LIGHT}
            title={t('Job ad introduction title')}
            buttonLinkHref={urlResolver({ path: URL_ACCOUNT_REGISTRATION })}
            buttonLinkEvent={analyticsEvents.TRACK_CTA}
            buttonLinkText={t('Create a job ad button')}
          >
            <Column>
              <IconContentBox
                icon={<Icon1 />}
                text={t('Job ad introduction find best person message')}
              />
            </Column>
            <Column>
              <IconContentBox
                icon={<Icon2 />}
                text={t('Job ad introduction connect relevant people message')}
              />
            </Column>
            <Column>
              <IconContentBox
                icon={<Icon3 />}
                text={t(
                  'Job ad introduction quality candidates listed first message',
                )}
              />
            </Column>
          </ContentSplitThreeColumn>

          <ImageContentSplit
            id="multipurpose"
            theme={ModuleTheme.DARK}
            title={t('Talent Search introduction title')}
            text={t('Talent Search introduction description')}
            imageSrc={multipurposeImage}
            buttonLinkHref={urlResolver({ path: URL_ACCOUNT_REGISTRATION })}
            buttonLinkEvent={analyticsEvents.TRACK_CTA}
            buttonLinkText={t('Talent Search introduction button')}
          />

          <ImageContentSplit
            id="media-advice"
            theme={ModuleTheme.LIGHT}
            reverse={true}
            title={t('Hiring Advice title')}
            text={t('Hiring Advice description')}
            imageSrc={mediaAdviceImage}
            buttonLinkHref={urlResolver({ path: URL_BLOG_HIRING_ADVICE_ROOT })}
            buttonLinkEvent="advice-clicked-lnk"
            buttonLinkText={t('Hiring Advice learn more button')}
            linkHref={urlResolver({ path: URL_ACCOUNT_REGISTRATION })}
            linkEvent={analyticsEvents.TRACK_CTA}
            linkText={t('Hiring Advice button')}
          />

          <MarketingBanner id="marketing-campaign-covid19-resources" />
        </>
      )}
    </>
  );
};

export default HomePage;
